import { Alert, Checkbox, Stack, Textarea, TextInput } from "@mantine/core"
import { observer } from "mobx-react-lite"
import { useNavigate, useParams } from "react-router-dom"
import { InstructorStore } from "../../../app/store"
import { yupResolver } from "@mantine/form"
import { type InstructorRequest } from "../../../shared/types/instructor"
import { validationSchema } from "./config"
import { useForm, FormProvider } from "./formContext"
import { useEffect } from "react"
import { PATHS } from "../../../shared/lib/paths"
import { IconAlertCircle } from "@tabler/icons-react"
import { ImageDropzone } from "../../../shared/ui/ImageDropzone"
import { SocialFields } from "./SocialFields"
import { FormActions } from "./FormActions"

// ----------------------------------------------------------------------

export const InstructorEditForm = observer(
  function InstructorEditForm() {
    const navigate = useNavigate()
    const { instructorId } = useParams<{ instructorId?: string }>()

    const { createInstructor, updateInstructor, loadInstructor, error } = InstructorStore

    const form = useForm({
      validateInputOnChange: true,
      validate: yupResolver(validationSchema),
      initialValues: {
        name: "",
        position: "",
        socials: [],
        published: true,
        avatar: "",
      },
    })

    useEffect(() => {
      if (instructorId) {
        void loadInstructor(instructorId).then((instructor) => {
          if (instructor) {
            const { _id, createdAt, updatedAt, ...dto } = instructor

            form.setValues(dto)
            form.resetDirty(dto)
          }
        })
      }

      return () => {
        form.reset()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ instructorId ])

    const onSubmit = (values: InstructorRequest): void => {
      const formData = new FormData()

      Object.entries(values).forEach(([ key, value ]) => {
        if ((instructorId && form.isDirty(key)) || !instructorId) {

          switch (key) {
            case "socials": {
              formData.append(key, JSON.stringify(value))
              break
            }
            case "published": {
              formData.append(key, String(value))
              break
            }
            default: {
              formData.append(key, value as string | File)
              break
            }
          }
        }
      })

      if (instructorId) {
        void updateInstructor(instructorId, formData).then(success => {
          if (success) {
            navigate(PATHS.DASHBOARD.INSTRUCTORS.ROOT)
          }
        })
      } else {
        void createInstructor(formData).then(success => {
          if (success) {
            navigate(PATHS.DASHBOARD.INSTRUCTORS.ROOT)
          }
        })
      }
    }

    return (
      <FormProvider form={form}>
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack spacing="xl">
            <TextInput
              label="Фамилия и имя преподавателя"
              placeholder="Василий Иванов"
              {...form.getInputProps("name")}
            />

            <Textarea
              label="Должность"
              placeholder="Врач ЛФК и спортивной медицины"
              {...form.getInputProps("position")}
              minRows={2}
              autosize
            />

            <ImageDropzone formValues={form.getInputProps("avatar")}/>

            <SocialFields/>

            <Checkbox
              label="Опубликовать преподавателя"
              {...form.getInputProps("published", { type: "checkbox" })}
            />
          </Stack>

          {error && (
            <Alert
              mt="xl"
              icon={<IconAlertCircle size="1rem"/>}
              title="Ошибка добавления преподавателя"
              color="red"
            >
              {error}
            </Alert>
          )}

          <FormActions/>
        </form>
      </FormProvider>
    )
  },
)
